import { useState, useCallback } from 'react';
import {convertBuyCostToFixed, fetchVariableBuyCosts, updateVariableBuyCosts} from "@/api/buy-cost";
import { VariableBuyCostBody, BuyCostEntry } from "@boxem/core-api-schema";
import {useInventoryContext} from "@/contexts/InventoryContext";
import {useBuyCostContext} from "@/contexts/BuyCostContext";

export const useBuyCosts = (listingId: number) => {
    const { fetchCategories } = useBuyCostContext();
    const { refetchWholeInventory } = useInventoryContext();
    const [buyCosts, setBuyCosts] = useState<BuyCostEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [updateSuccess, setUpdateSuccess] = useState(false);

    const fetchBuyCosts = useCallback(async () => {
        setLoading(true);
        setError(null);
        setUpdateSuccess(false);
        try {
            const response = await fetchVariableBuyCosts(listingId);
            setBuyCosts(response.buyCosts);
        } catch (err) {
            setError('Failed to fetch buy costs. Please try again.');
            console.error('Error fetching buy costs:', err);
        } finally {
            setLoading(false);
        }
    }, [listingId]);

    const updateBuyCosts = useCallback(async (updatedBuyCosts: BuyCostEntry[]) => {
        setLoading(true);
        setError(null);
        setUpdateSuccess(false);
        try {
            const payload: VariableBuyCostBody = {
                buyCosts: updatedBuyCosts,
            };
            await updateVariableBuyCosts(listingId, payload);
            setUpdateSuccess(true);
            // Update the local state with the new data
            setBuyCosts(updatedBuyCosts);
            refetchWholeInventory();
            fetchCategories();
        } catch (err) {
            setError('Failed to update buy costs. Please try again.');
            console.error('Error updating buy costs:', err);
        } finally {
            setLoading(false);
        }
    }, [listingId]);

    const convertToFixedBuyCost = useCallback(async (listingId: number) => {
        setLoading(true);
        setError(null);
        setUpdateSuccess(false);
        try {
            await convertBuyCostToFixed(listingId);
            setUpdateSuccess(true);
            refetchWholeInventory();
        }
        catch(err){
            setError('Failed to convert to fixed buy cost. Please try again.');
            console.error('Error updating buy costs:', err);
        }
        finally {
            setLoading(false);
        }
    }, []);

    return { buyCosts, loading, error, updateSuccess, fetchBuyCosts, updateBuyCosts, convertToFixedBuyCost };
};